import { useState } from 'react';
import { StarIcon } from '@heroicons/react/24/outline';
import { useRespect } from '../../hooks/useRespect';
import { RespectBreakdownPanel } from './RespectBreakdownPanel';
import type { RespectBand } from '../../services/api/respect';

interface RespectBandBadgeProps {
  userId: number;
}

const BAND_STYLES: Record<RespectBand, { pill: string; label: string; showStar: boolean }> = {
  small: {
    pill: 'bg-gray-100 text-gray-700',
    label: 'Respect: Small',
    showStar: false,
  },
  medium: {
    pill: 'bg-emerald-100 text-emerald-800',
    label: 'Respect: Medium',
    showStar: false,
  },
  'spotlight-level': {
    pill: 'bg-amber-200 text-amber-900',
    label: 'Spotlight',
    showStar: true,
  },
};

export function RespectBandBadge({ userId }: RespectBandBadgeProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const { data, isLoading } = useRespect(userId);

  if (isLoading) {
    return (
      <div
        role="status"
        data-testid="band-skeleton"
        className="h-6 w-24 animate-pulse rounded-full bg-gray-200"
        aria-label="Loading respect band"
      />
    );
  }

  const band = data?.band ?? 'small';
  const style = BAND_STYLES[band];

  return (
    <>
      <button
        type="button"
        onClick={() => setPanelOpen(true)}
        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-opacity hover:opacity-80 ${style.pill}`}
        aria-label={`${style.label} — click to see breakdown`}
      >
        {style.showStar && <StarIcon className="h-3.5 w-3.5" aria-hidden="true" />}
        {style.label}
      </button>
      {panelOpen && data && (
        <RespectBreakdownPanel
          data={data}
          onClose={() => setPanelOpen(false)}
        />
      )}
    </>
  );
}
